import React, { Component } from "react";
import PropTypes from "prop-types";
import { Button, notification, Modal, Checkbox, Alert, Tooltip, Upload, message, Form, Radio } from "antd";
import { ExportOutlined, UploadOutlined } from "@ant-design/icons";
import { exportCSV } from "../../services/api";
import { withUser } from "@unifiedpatents/sso";
import { API_URL } from "../../config";

const CheckboxGroup = Checkbox.Group;

const XlsxUpload = ({ email, onChange }) => {
  const uploadProps = {
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    action: `${API_URL}/helpers/upload-file`,
    headers: {
      "x-email": email
    },
    maxCount: 1,
    beforeUpload: file => {
      if (file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        message.error(`${file.name} is not a xlsx file`);
        return false;
      }
      return file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ? true : Upload.LIST_IGNORE;
    },
    onChange
  };
  return (
    <Upload {...uploadProps}>
      <Button icon={<UploadOutlined />}>Import SPIF / Pub. Number (xlsx only)</Button>
    </Upload>
  )
};
XlsxUpload.propTypes = {
  email: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

class ExportButton extends Component {
  static propTypes = {
    url: PropTypes.string.isRequired,
    queries: PropTypes.object.isRequired, // raw ES query, not URL query params
    columns: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      dataIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
      key: PropTypes.string.isRequired
    })).isRequired,
    columnKeysSelected: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    helpText: PropTypes.string,
    xlsxImportEnabled: PropTypes.bool,
    quotaExceeded: PropTypes.bool,
    quota: PropTypes.number,
  };

  static defaultProps = {
    disabled: false,
    label: "Export All Pages",
    xlsxImportEnabled: false,
    quotaExceeded: false,
    quota: 100000
  };

  state = {
    loading: false,
    modalVisible: false,
    selectedCols: [],
    allChecked: false,
    allIndeterminate: true,
    xlsxFileName: undefined,
    format: "csv"
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { columns, disabled, label, helpText, style, xlsxImportEnabled, quotaExceeded, quota } = this.props;
    const { selectedCols, allChecked, allIndeterminate } = this.state;
    const options = columns.map(item => ({ label: item.title, value: item.key }));
    const noColSelected = selectedCols.length === 0;
    return (
      (<span style={style}>
        <Tooltip title={helpText} placement="bottom">
          <Button onClick={this._handleClick} icon={<ExportOutlined />} size="small" loading={this.state.loading} disabled={disabled}>
            {label}
          </Button>
        </Tooltip>
        <Modal
          title="Select columns to export"
          open={this.state.modalVisible}
          onOk={this._handleModalOk}
          okButtonProps={{ disabled: noColSelected || quotaExceeded }}
          onCancel={this._handleModalCancel}
        >
          {xlsxImportEnabled ? <XlsxUpload email={this.props.user.profile.email} onChange={this._handleUploadOnChange} /> : null}
          <br />
          <Checkbox checked={allChecked} indeterminate={allIndeterminate} onChange={this._handleCheckAll}>
            {"Select All"}
          </Checkbox>
          <br />
          <br />
          <CheckboxGroup options={options} value={selectedCols} onChange={this._handleSelectChange} />
          <br />
          <br />
          <Form.Item label="File Format:">
            <Radio.Group name="radiogroup" defaultValue={"csv"} value={this.state.format} onChange={this._updateFormat}>
              <Radio value={"csv"}>csv</Radio>
              <Radio value={"xlsx"}>xlsx</Radio>
            </Radio.Group>
          </Form.Item>
          {noColSelected ? <Alert style={{ marginTop: 8 }} message="Please select at least one column for the CSV" type="error" showIcon={true} /> : ""}
          {quotaExceeded ? <Alert style={{ marginTop: 8 }} message={`You have exceeded the limit of ${quota} records. Please narrow down your search or contact support.`} type="error" showIcon={true} /> : ""}
        </Modal>
      </span>)
    );
  }

  _handleUploadOnChange = info => {
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      this.setState({ xlsxFileName: info.file.name });
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    } else if (info.file.status === "removed") {
      this.setState({ xlsxFileName: undefined });
    }
  };

  _updateFormat = e => {
    this.setState({ format: e.target.value });
  };

  _handleClick = () => {
    const { columnKeysSelected } = this.props;
    this.setState({
      modalVisible: true,
      selectedCols: columnKeysSelected
    });
  };

  _handleSelectChange = value => {
    this.setState({
      selectedCols: value,
      allIndeterminate: !!value.length && value.length < this.props.columns.length,
      allChecked: value.length === this.props.columns.length
    });
  };

  _handleCheckAll = e => {
    this.setState({
      selectedCols: e.target.checked ? this.props.columns.map(item => item.key) : [],
      allIndeterminate: false,
      allChecked: e.target.checked
    });
  };

  _handleModalOk = () => {
    const { url, queries, columns } = this.props;
    const { email } = this.props.user.profile;
    const { selectedCols, format, xlsxFileName } = this.state;
    // derive string representations of dataIndices of columns with key covered by selectedCols
    const cols = columns.reduce((acc, col) => {
      if (selectedCols.includes(col.key)) {
        if (Array.isArray(col.dataIndex)) {
          acc.push(col.dataIndex.join("."));
        } else {
          acc.push(col.dataIndex);
        }
      }
      return acc;
    }, []);
    if (this._isMounted) this.setState({ loading: true });
    exportCSV(url, queries, cols, email, format, xlsxFileName)
      .then(json => {
        if (json.statusCode === 200) {
          this._showSuccessNotif();
        } else {
          this._showErrorNotif();
        }
        if (this._isMounted) this.setState({ loading: false });
      })
      .catch(() => {
        this._showErrorNotif();
        if (this._isMounted) this.setState({ loading: false });
      });
    this.setState({ modalVisible: false });
  };

  _handleModalCancel = () => {
    this.setState({ modalVisible: false });
  };

  _showSuccessNotif = () => {
    notification.success({
      message: "Your request for CSV export has been scheduled.",
      description: "You will be informed by email once the CSV is ready, it may take up to an hour to generate your CSV depending on your data size.",
      duration: 0,
      key: "csv-export-scheduled"
    });
  };

  _showErrorNotif = () => {
    notification.error({
      message: "We are unable to schedule your request for CSV export.",
      description: "Something went wrong, please contact support if the issue persists.",
      duration: 0,
      key: "csv-export-not-scheduled"
    });
  };
}

export default withUser(ExportButton);
